import { useState } from "react";
import { Globe } from "lucide-react";
import { api } from "@/lib/api";
import AddressSearch from "@/components/landing/AddressSearch";
import SoilTempTool from "@/components/landing/SoilTempTool";
import GddTool from "@/components/landing/GddTool";

export default function LiveClimate() {
  const [loc, setLoc] = useState(null);
  const [gs, setGs] = useState("warm");
  const [soil, setSoil] = useState({ data: null, loading: false, error: "" });
  const [gdd, setGdd] = useState({ data: null, loading: false, error: "" });

  const fetchSoil = async (l) => {
    setSoil({ data: null, loading: true, error: "" });
    try {
      const res = await api.get("/tools/soil-temp", { params: { lat: l.lat, lon: l.lon, place: l.label } });
      setSoil({ data: res.data, loading: false, error: "" });
    } catch (err) {
      setSoil({ data: null, loading: false, error: err?.response?.data?.detail || "Could not fetch soil data." });
    }
  };

  const fetchGdd = async (l, season) => {
    setGdd({ data: null, loading: true, error: "" });
    try {
      const res = await api.get("/tools/gdd", { params: { lat: l.lat, lon: l.lon, place: l.label, gs: season } });
      setGdd({ data: res.data, loading: false, error: "" });
    } catch (err) {
      setGdd({ data: null, loading: false, error: err?.response?.data?.detail || "Weather data unavailable, try again shortly." });
    }
  };

  const onSelect = (l) => {
    setLoc(l);
    const season = Math.abs(l.lat) < 35 ? "warm" : "cool";
    setGs(season);
    fetchSoil(l);
    fetchGdd(l, season);
  };

  const onSeasonChange = (season) => {
    setGs(season);
    if (loc) fetchGdd(loc, season);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="relative z-20 rounded-3xl border border-[var(--ld-line)] bg-[var(--ld-surface)]/80 p-5 backdrop-blur-xl sm:p-6">
        <div className="mb-3 flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.22em] text-[var(--ld-lime)]">
          <Globe className="h-3.5 w-3.5" /> Your location · worldwide
        </div>
        <AddressSearch onSelect={onSelect} idPrefix="climate-loc" placeholder="Search your city, town or address…" />
        <p className="mt-2.5 text-xs font-semibold text-[var(--ld-muted)]">
          {loc ? `Showing live data for ${loc.label}.` : "Search once — live soil temperature and season GDD update together."}
        </p>
      </div>

      <SoilTempTool idPrefix="soil-temp-hero" data={soil.data} loading={soil.loading} error={soil.error} hasLocation={!!loc} />
      <GddTool
        idPrefix="gdd-hero"
        data={gdd.data}
        loading={gdd.loading}
        error={gdd.error}
        gs={gs}
        onSeasonChange={onSeasonChange}
        onRetry={() => loc && fetchGdd(loc, gs)}
        hasLocation={!!loc}
      />
    </div>
  );
}
